// ---------------------------------------------------------------------------
// V29 — RISK RADAR. WHAT IS ABOUT TO GO WRONG, AND WHAT TO DO ABOUT IT.
//
// `console.events.risk`, at `events/:id/risk` beside the board and the cascade
// that act on it.
//
// ---------------------------------------------------------------------------
// THIS IS NOT A WARNING LIGHT
//
// A warning light tells a scheduler what they can already see. Every row here
// carries THE ACTION as well as the score, because a number with no move beside
// it is an anxiety generator. The actions are ranked by what they are expected to
// recover per unit of cost, and both of those figures came from the model rather
// than from this file.
//
// ---------------------------------------------------------------------------
// CONFIDENCE IS ON EVERY ROW AND IT IS NEVER DECORATION
//
// §V29.4: a prediction that hides its own ignorance is worse than no prediction.
// So the confidence chip sits at the same weight as the score, its tone comes
// from the database (`0072`: the colour of a state is a property of the state),
// and the row says how many of the ten terms could be measured. A screen showing
// 74 with nothing beside it would be read as 74 out of a hundred certain.
//
// ---------------------------------------------------------------------------
// THE TERMS THAT COULD NOT BE MEASURED ARE ON THE SCREEN
//
// Two of the ten never can be in this build, and drawing them as zero would
// report every post as calmer than it is. They render with no bar and the
// sentence that says why, which is the same rule V26's travel factor established
// and the same reason: an absence nobody can see is indistinguishable from a
// promise nobody kept.
//
// ---------------------------------------------------------------------------
// THE DECISION CONTROL IS ON EVERY FORECAST, IN EVERY STATE
//
// Including the calm ones. §V29.6.5 and `0075`'s decision 5: a human review route
// available only where the machine was worried is a rubber stamp. It is hidden
// only from somebody who does not hold `risk.override`, and the database asks the
// same question again.
//
// NO NEW CSS. Every class here already carries a rule in `web/src/design`.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { send } from "../api/auth";
import {
  risk as riskApi,
  type RiskActionLine,
  type RiskFactorLine,
  type RiskOverride,
  type RiskPrediction,
  type RiskRun,
  type RiskVocabulary,
} from "../api/risk";
import { Notice } from "../design/Notice";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { money as formatMoney, whenIn } from "../system/format";

/** The four tones the design system knows. Anything else is drawn as neutral. */
const TONES = ["neutral", "live", "attention", "failed"] as const;
type Tone = (typeof TONES)[number];
const toneOf = (t: string): Tone =>
  (TONES as readonly string[]).includes(t) ? (t as Tone) : "neutral";

export function RiskRadar() {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const tl = (text: string) => translateLabel(locale, text);
  const params = useParams();
  const eventId = params["id"] ?? "";

  // THE ORGANISATION'S CLOCK, AND EVERY TIME ON THIS SCREEN GOES THROUGH IT.
  // `HANDOFF.md` §6's ruling, built here first because this is the screen where
  // it became visible: the `WHEN` column said 06:00 while the working underneath
  // said 05:00, on a page about early starts. `/v1/me` already carries the
  // organisation's zone — `me.ts` reads it off `app.organisation.timezone`, the
  // same column `0084` resolves before it decides what a daypart is — so no
  // endpoint had to change. The narrowing is at the point of use, which is what
  // the note on `Me.active_org` in `api/auth.ts` asks for.
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;

  const [vocab, setVocab] = useState<RiskVocabulary | null>(null);
  const [vocabFailed, setVocabFailed] = useState(false);
  const [runs, setRuns] = useState<RiskRun[] | null>(null);
  const [mayAssess, setMayAssess] = useState(false);
  const [mayDecide, setMayDecide] = useState(false);
  const [runId, setRunId] = useState<string>("");
  const [predictions, setPredictions] = useState<RiskPrediction[] | null>(null);
  const [overrides, setOverrides] = useState<RiskOverride[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [factors, setFactors] = useState<RiskFactorLine[] | null>(null);
  const [actions, setActions] = useState<RiskActionLine[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      const res = await send<RiskVocabulary & { error?: { message: string } }>(
        riskApi.vocabulary(apiBase),
      );
      if (res.status === 200 && Array.isArray(res.body?.risk_override_actions)) {
        setVocab(res.body);
        setVocabFailed(false);
      } else {
        // V26's finding, and it applies here for the same legal reason. With no
        // acts to render the decision controls simply vanish and the page looks
        // completely normal — a tidy, working forecast with its human review route
        // silently absent, indistinguishable from a scheduler who lacks the
        // permission and from everything working. An absence is something a bug
        // produces by accident; a refusal is a fact.
        setVocabFailed(true);
      }
    })();
  }, [apiBase]);

  const loadRuns = useCallback(async () => {
    const res = await send<{
      runs?: RiskRun[];
      may_assess?: boolean;
      may_decide?: boolean;
      error?: { message: string };
    }>(riskApi.runs(apiBase, eventId));
    // THE PAYLOAD IS AT THE TOP LEVEL. `ok()` wraps nothing, and V24 shipped four
    // screens that reached inside for a key the Worker does not produce.
    if (res.status === 200 && Array.isArray(res.body?.runs)) {
      setRuns(res.body.runs);
      setMayAssess(res.body.may_assess === true);
      setMayDecide(res.body.may_decide === true);
      setRunId(res.body.runs[0]?.run_id ?? "");
      setFailed(null);
    } else {
      setFailed(refusal(res.body?.error) ?? t("The forecasts for this event could not be read."));
    }
  }, [apiBase, eventId]);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  const loadPredictions = useCallback(async () => {
    if (!runId) {
      setPredictions(null);
      setOverrides([]);
      return;
    }
    const res = await send<{
      predictions?: RiskPrediction[];
      overrides?: RiskOverride[];
      error?: { message: string };
    }>(riskApi.run(apiBase, runId));
    if (res.status === 200 && Array.isArray(res.body?.predictions)) {
      setPredictions(res.body.predictions);
      setOverrides(res.body.overrides ?? []);
      setFailed(null);
    } else {
      setFailed(refusal(res.body?.error) ?? t("This forecast could not be read."));
    }
  }, [apiBase, runId]);

  useEffect(() => {
    void loadPredictions();
  }, [loadPredictions]);

  const assess = useCallback(async () => {
    setBusy(true);
    const res = await send<{ risk_run?: { run_id: string }; error?: { message: string } }>(
      riskApi.assess(apiBase, eventId),
    );
    setBusy(false);
    if (res.status >= 400 || !res.body?.risk_run) {
      setFailed(refusal(res.body?.error) ?? t("That forecast could not be taken."));
      return;
    }
    setSaid(t("Assessed. This is a record of an automated assessment and cannot be edited."));
    await loadRuns();
  }, [apiBase, eventId, loadRuns]);

  const score = useCallback(async () => {
    setBusy(true);
    const res = await send<{ scored?: number; error?: { message: string } }>(
      riskApi.score(apiBase, runId),
    );
    setBusy(false);
    if (res.status >= 400) {
      setFailed(refusal(res.body?.error) ?? t("That forecast could not be scored."));
      return;
    }
    // THE COUNT IS THE ANSWER AND ZERO IS A REAL ONE. Scoring a forecast whose
    // shifts have not happened yet does nothing, and a message saying "scored"
    // would tell a scheduler an accuracy figure exists when it does not.
    const n = res.body?.scored ?? 0;
    setSaid(
      n === 0
        ? t("Nothing to score yet: none of these shifts has finished, and scoring one before it happens would flatter the model.")
        : t("Scored {n} post(s) against what actually happened.", { n }),
    );
    await loadRuns();
    await loadPredictions();
  }, [apiBase, runId, loadRuns, loadPredictions]);

  const showDetail = useCallback(
    async (predictionId: string) => {
      if (open === predictionId) {
        setOpen(null);
        return;
      }
      setOpen(predictionId);
      setFactors(null);
      setActions(null);
      const res = await send<{
        factors?: RiskFactorLine[];
        actions?: RiskActionLine[];
        error?: { message: string };
      }>(riskApi.prediction(apiBase, predictionId));
      if (res.status === 200 && Array.isArray(res.body?.factors)) {
        setFactors(res.body.factors);
        setActions(res.body.actions ?? []);
      } else {
        setFailed(refusal(res.body?.error) ?? t("That breakdown could not be read."));
      }
    },
    [apiBase, open],
  );

  const decide = useCallback(
    async (predictionId: string, action: string, riskActionId: string | null) => {
      setBusy(true);
      const res = await send<{ error?: { message: string } }>(
        riskApi.decide(apiBase, predictionId, { action, risk_action_id: riskActionId }),
      );
      setBusy(false);
      if (res.status >= 400) {
        setFailed(refusal(res.body?.error) ?? t("That decision could not be recorded."));
        return;
      }
      setSaid(t("Recorded. The forecast is unchanged; what you decided sits beside it."));
      await loadPredictions();
      if (open !== null) {
        const again = await send<{ factors?: RiskFactorLine[]; actions?: RiskActionLine[] }>(
          riskApi.prediction(apiBase, open),
        );
        if (again.status === 200 && Array.isArray(again.body?.factors)) {
          setFactors(again.body.factors);
          setActions(again.body.actions ?? []);
        }
      }
    },
    [apiBase, loadPredictions, open],
  );

  const current = runs?.find((r) => r.run_id === runId) ?? null;
  // THE ACTS COME FROM THE DATABASE. `needs_action` decides which control belongs
  // on a whole forecast and which on one recommendation, so a fourth act added
  // next year appears here without this file moving.
  const wholeForecastActs = (vocab?.risk_override_actions ?? []).filter((a) => !a.needs_action);
  const perActionActs = (vocab?.risk_override_actions ?? []).filter((a) => a.needs_action);

  /**
   * What a person decided, in the words the database holds.
   *
   * The fallback is the bare id rather than a generic phrase, for V26's reason: a
   * word a reader does not recognise sends them to ask, and "A person decided"
   * sends them nowhere while hiding WHICH decision was taken.
   */
  const decidedLabel = (actionId: string | null): string => {
    if (actionId === null) return "";
    const found = (vocab?.risk_override_actions ?? []).find((a) => a.id === actionId);
    return found ? found.label : actionId;
  };

  // S42. The product's one formatter, in the organisation's currency, rather than a
  // local division that assumed two decimals and named no currency at all.
  const orgCurrency = me !== null && me.authenticated ? (me.active_org?.currency ?? null) : null;
  const money = (minor: string): string =>
    /^-?0*$/.test(minor.trim())
      ? t("no extra cost")
      : t("{amount} more", { amount: orgCurrency ? formatMoney(minor, orgCurrency) : t("{n} minor units", { n: minor }) });

  const assessButton = mayAssess ? (
    <div className="row">
      <button type="button" className="btn" disabled={busy} onClick={() => void assess()}>
        {t("Assess this event now")}
      </button>
    </div>
  ) : null;

  const anyDecided = (predictions ?? []).some((p) => p.decided);
  return (
    <div className="stack">
      <h1>{t("Risk Radar")}</h1>
      <p className="muted">
        {t("A forecast of which posts on this event will not fill, and what can still be done about it. Nothing here decides anything on its own — every line says what it was built from, and what you decide is recorded beside it whether it agrees or not.")}
      </p>
      <p className="muted">{t("Risk Radar is an explainable rule of thumb, not a prediction.")}</p>
      {failed !== null ? <StatusChip tone="failed" label={failed} /> : null}
      {said !== null ? <StatusChip tone="live" label={said} /> : null}
      {vocabFailed ? (
        <Notice tone="failed">{t("The decision controls could not be loaded, so nothing on this page can be overridden. Reload before you act on this forecast.")}</Notice>
      ) : null}

      {runs !== null && runs.length === 0 ? (
        <section className="panel stack">
          <p className="muted">
            {t("Nothing has been forecast for this event yet. A forecast is worth taking a few days out, while widening a wave or promoting a reserve is still cheap.")}
          </p>
          {assessButton}
        </section>
      ) : (
        assessButton
      )}

      {runs !== null && runs.length > 0 ? (
        <section className="panel">
          <div className="field">
            <label htmlFor="v29-run">{t("Forecast")}</label>
            <select id="v29-run" value={runId} onChange={(e) => setRunId(e.target.value)}>
              {runs.map((r) => (
                <option key={r.run_id} value={r.run_id}>
                  {whenIn(r.assessed_at, orgZone)} · {t("{h}h out · {n} post(s)", { h: r.horizon_hours, n: r.requirement_count })}
                </option>
              ))}
            </select>
          </div>
          {current !== null ? (
            <>
              <p className="muted">
                {t("Taken {h} hour(s) before the event, on model {model}. A record of an automated assessment: it cannot be edited — assessing again makes a new one — and it is kept until {until}.", {
                  h: current.horizon_hours,
                  model: current.model_version,
                  until: whenIn(current.retain_until, orgZone),
                })}
              </p>
              {/* HOW WRONG IT WAS, WHERE THAT IS KNOWN. §V29.6.2 asks for accuracy to
                  be measurable AND published, and a module that publishes only its
                  predictions is publishing the flattering half. */}
              <p className="muted">
                {current.scored_count === 0
                  ? t("None of these posts has finished yet, so there is no accuracy figure for this forecast.")
                  : t("Scored against {n} finished post(s): out by {error} seat(s) on average.", {
                      n: current.scored_count,
                      error: current.mean_absolute_error ?? "—",
                    })}
              </p>
              {current.history_shifts === 0 ? (
                <Notice tone="attention">{t("This organisation has no completed shifts behind it, so every forecast here is low confidence. That is the honest answer, not a fault.")}</Notice>
              ) : null}
              {mayAssess ? (
                <button
                  type="button"
                  className="btn"
                  disabled={busy || !runId}
                  onClick={() => void score()}
                >
                  {t("Score this forecast against what happened")}
                </button>
              ) : null}
            </>
          ) : null}
        </section>
      ) : null}

      {predictions !== null && predictions.length > 0 ? (
        <section className="panel">
          <h2>{t("Posts, worst first")}</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t("Post")}</th>
                <th scope="col">{t("When")}</th>
                <th scope="col" className="numeric">
                  {t("Risk")}
                </th>
                <th scope="col" className="numeric">
                  {t("Seats short")}
                </th>
                <th scope="col">{t("How sure")}</th>
                <th scope="col">{t("Why")}</th>
                {anyDecided ? <th scope="col">{t("Decided")}</th> : null}
                <th scope="col">{""}</th>
              </tr>
            </thead>
            <tbody>
              {predictions.map((p) => (
                <>
                  <tr key={p.prediction_id}>
                    <td>{p.zone ?? translateLabel(locale, p.role_name)}</td>
                    <td>{whenIn(p.starts_at, orgZone)}</td>
                    <td className="numeric">{Number(p.risk).toFixed(1)}</td>
                    <td className="numeric">
                      {t("{short} of {open} open", { short: p.predicted_shortfall, open: p.open_seats })}
                      {p.actual_shortfall !== null ? ` · ${t("was {n}", { n: p.actual_shortfall })}` : ""}
                    </td>
                    <td>
                      {/* NEVER COLOUR ALONE. The chip carries the words as well as
                          the tone, because this is read on a cracked phone in the
                          rain — and because a colour-blind reader would otherwise be
                          told nothing at all about how sure the number is. */}
                      <StatusChip tone={toneOf(p.confidence_tone)} label={tl(p.confidence_label)} />
                      <div className="muted">
                        {t("{n} of {total} terms measured", { n: p.factors_measured, total: vocab?.risk_factors.length ?? 10 })}
                      </div>
                    </td>
                    {/* ONE SENTENCE ON THE ROW AND TEN BEHIND THE CONTROL.
                        The gallery is what found the alternative: the whole
                        explanation in a table cell is forty rows of ten
                        sentences on a stadium turnaround, which is the wall
                        V26's own comment warns about. `headline` is chosen by
                        the model, beside the score it summarises. */}
                    <td>{p.headline}</td>
                    {anyDecided ? <td>{p.decided ? decidedLabel(p.decided_action) : null}</td> : null}
                    <td>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => void showDetail(p.prediction_id)}
                      >
                        {open === p.prediction_id ? t("Hide the working") : t("Show the working")}
                      </button>
                    </td>
                  </tr>
                  {open === p.prediction_id ? (
                    <tr key={`${p.prediction_id}-detail`}>
                      <td colSpan={anyDecided ? 8 : 7}>
                        {factors === null ? (
                          <p className="muted">{t("Reading the working…")}</p>
                        ) : (
                          <>
                            <p className="muted">{p.explanation}</p>
                            <h3>{t("Every term, including the ones that could not be measured")}</h3>
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th scope="col">{t("Term")}</th>
                                  <th scope="col" className="numeric">
                                    {t("Weight")}
                                  </th>
                                  <th scope="col" className="numeric">
                                    {t("Adds to the score")}
                                  </th>
                                  <th scope="col">{t("What it says")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {factors.map((f) => (
                                  <tr key={f.factor_id}>
                                    <td>{tl(f.factor_label)}</td>
                                    <td className="numeric">{f.weight}</td>
                                    <td className="numeric">
                                      {/* AN UNMEASURED TERM SHOWS A DASH AND NEVER A
                                          ZERO. Zero would say it was measured and
                                          found calm, which is a different and much
                                          more confident claim. */}
                                      {f.is_measurable ? Number(f.contribution).toFixed(2) : t("not measured")}
                                    </td>
                                    <td>{f.sentence}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <p className="muted">
                              These add up to {Number(p.risk).toFixed(1)}, which is the score
                              above. Nothing else goes into it.
                            </p>

                            <h3>{t("What can still be done")}</h3>
                            {(actions ?? []).length === 0 ? (
                              <p className="muted">
                                {t("Nothing this product can offer would move this post: either every place is taken, or the terms driving the risk are ones no action here addresses. That is worth knowing rather than hiding.")}
                              </p>
                            ) : (
                              <table className="data-table">
                                <thead>
                                  <tr>
                                    <th scope="col">{t("Move")}</th>
                                    <th scope="col" className="numeric">
                                      {t("Expected gain")}
                                    </th>
                                    <th scope="col">{t("Cost")}</th>
                                    <th scope="col">{t("What it means")}</th>
                                    <th scope="col">{t("Decided")}</th>
                                    <th scope="col">{""}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(actions ?? []).map((a) => (
                                    <tr key={a.action_id}>
                                      <td>{tl(a.action_label)}</td>
                                      <td className="numeric">{t("{n} seat(s)", { n: a.expected_gain })}</td>
                                      <td>{money(a.cost_delta_minor)}</td>
                                      <td>{a.sentence}</td>
                                      <td>
                                        {a.decided ? (
                                          <StatusChip
                                            tone="live"
                                            label={`${decidedLabel(a.decided_action)} · ${whenIn(a.decided_at ?? "", orgZone)}`}
                                          />
                                        ) : (
                                          "—"
                                        )}
                                      </td>
                                      <td>
                                        {mayDecide
                                          ? perActionActs.map((act) => (
                                              <button
                                                key={act.id}
                                                type="button"
                                                className="btn"
                                                disabled={busy}
                                                onClick={() =>
                                                  void decide(p.prediction_id, act.id, a.action_id)
                                                }
                                              >
                                                {tl(act.label)}
                                              </button>
                                            ))
                                          : null}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}

                            {mayDecide
                              ? wholeForecastActs.map((act) => (
                                  <button
                                    key={act.id}
                                    type="button"
                                    className="btn"
                                    disabled={busy}
                                    onClick={() => void decide(p.prediction_id, act.id, null)}
                                  >
                                    {tl(act.label)}
                                  </button>
                                ))
                              : null}
                          </>
                        )}
                      </td>
                    </tr>
                  ) : null}
                </>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {predictions !== null && predictions.length === 0 && runId !== "" ? (
        <section className="panel">
          <p className="muted">
            {t("This forecast covers no posts, which means the event has none yet. There is nothing to be short of.")}
          </p>
        </section>
      ) : null}

      {overrides.length > 0 ? (
        <section className="panel">
          <h2>{t("What people decided")}</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t("When")}</th>
                <th scope="col">{t("Decision")}</th>
                <th scope="col">{t("Reason")}</th>
              </tr>
            </thead>
            <tbody>
              {overrides.map((o) => (
                <tr key={o.override_id}>
                  <td>{whenIn(o.decided_at, orgZone)}</td>
                  <td>{tl(o.action_label)}</td>
                  <td>{o.reason ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </div>
  );
}
